import React, { useEffect, useState } from 'react';
import { getLimitDetails } from '../../services/LimitsService';
import './LimitDetailsModal.css';

function formatDate(value) {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString();
}

function Stat({ label, value }) {
    return (
        <div className="user-details-stat">
            <span className="user-details-stat-label">{label}</span>
            <span className="user-details-stat-value">{value}</span>
        </div>
    );
}

/**
 * props:
 * - limitId
 */
function LimitDetailsModal({ limitId }) {
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);

    useEffect(() => {
        if (!limitId) return undefined;

        setLoading(true);
        setError('');
        getLimitDetails(limitId, search, page)
            .then((result) => setDetails(result))
            .catch((err) => {
                console.error(err.response ? err.response.data : err.message);
                setError(err.response ? String(err.response.data) : err.message);
                setDetails(null);
            })
            .finally(() => setLoading(false));
    }, [limitId, search, page]);

    useEffect(() => {
        setSearchInput('');
        setSearch('');
        setPage(1);
        setDetails(null);
        setError('');
    }, [limitId]);

    function onSearchSubmit(event) {
        event.preventDefault();
        setPage(1);
        setSearch(searchInput.trim());
    }

    const limit = details?.limit || {};
    const history = details?.history || { count: 0, rows: [], pageSize: 10 };
    const totalPages = Math.max(1, Math.ceil((history.count || 0) / (history.pageSize || 10)));

    return (
        <div className="modal fade" id="modalLimitDetails" tabIndex="-1" role="dialog" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                <div className="modal-content user-details-modal">
                    <div className="modal-header">
                        <p className="modal-title">Limit details</p>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="close" />
                    </div>
                    <div className="modal-body">
                        {loading && !details ? <p className="user-details-status">Loading limit details…</p> : null}
                        {!loading && error ? <p className="user-details-status user-details-status--error">{error}</p> : null}

                        {details ? (
                            <>
                                <div className="user-details-identity">
                                    <div>
                                        <h3 className="user-details-name">{limit.name}</h3>
                                        <p className="user-details-email">
                                            {details.currentUserCount} current {details.currentUserCount === 1 ? 'user' : 'users'}
                                        </p>
                                    </div>
                                    <span className={`user-details-status-badge ${limit.isActive ? 'is-active' : 'is-inactive'}`}>
                                        {limit.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </div>

                                <section className="user-details-section">
                                    <h4>Plan caps</h4>
                                    <div className="user-details-grid">
                                        <Stat label="Max bots" value={limit.maxAutomations ?? 0} />
                                        <Stat label="Max monitors" value={limit.maxMonitors ?? 0} />
                                        <Stat label="Max backtests" value={limit.maxBacktests ?? 0} />
                                        <Stat label="Futures" value={limit.hasFutures ? 'Enabled' : 'Not included'} />
                                    </div>
                                </section>

                                <section className="user-details-section">
                                    <h4>Current users</h4>
                                    {details.currentUsers && details.currentUsers.length ? (
                                        <div className="limit-details-table-wrap">
                                            <table className="table table-hover limit-details-table">
                                                <thead>
                                                    <tr>
                                                        <th>User</th>
                                                        <th>Email</th>
                                                        <th>Since</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {details.currentUsers.map((user) => (
                                                        <tr key={user.id}>
                                                            <td>{user.name || '—'}</td>
                                                            <td>{user.email}</td>
                                                            <td>{formatDate(user.since)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <p className="user-details-status">No users currently on this limit.</p>
                                    )}
                                </section>

                                <section className="user-details-section">
                                    <h4>Assignment history</h4>
                                    <form className="limit-details-search" onSubmit={onSearchSubmit}>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search by name or email"
                                            value={searchInput}
                                            onChange={(event) => setSearchInput(event.target.value)}
                                        />
                                        <button type="submit" className="btn btn-primary btn-sm">Search</button>
                                    </form>

                                    {history.rows && history.rows.length ? (
                                        <div className="limit-details-table-wrap">
                                            <table className="table table-hover limit-details-table">
                                                <thead>
                                                    <tr>
                                                        <th>User</th>
                                                        <th>Email</th>
                                                        <th>Started</th>
                                                        <th>Ended</th>
                                                        <th>Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {history.rows.map((row) => (
                                                        <tr key={row.id}>
                                                            <td>{row.name || '—'}</td>
                                                            <td>{row.email}</td>
                                                            <td>{formatDate(row.startedAt)}</td>
                                                            <td>{formatDate(row.endedAt)}</td>
                                                            <td>{row.isCurrent ? 'Current' : 'Past'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <p className="user-details-status">
                                            {search ? 'No matching history for that search.' : 'No assignment history yet.'}
                                        </p>
                                    )}

                                    {history.count > (history.pageSize || 10) ? (
                                        <div className="limit-details-pagination">
                                            <button
                                                type="button"
                                                className="btn btn-secondary btn-sm"
                                                disabled={page <= 1}
                                                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                                            >
                                                Previous
                                            </button>
                                            <span>Page {page} of {totalPages}</span>
                                            <button
                                                type="button"
                                                className="btn btn-secondary btn-sm"
                                                disabled={page >= totalPages}
                                                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                                            >
                                                Next
                                            </button>
                                        </div>
                                    ) : null}
                                </section>
                            </>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default LimitDetailsModal;